import { FC, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { GuardProps } from '../../types/auth';
import { useSignOut } from '../../auth/useSignOut';
import { useSnackbar } from '../../store/snackbar';
import { useOrganizationDomainContext } from '../../contexts/OrganizationContext';
import { useIntl } from 'react-intl';
import { useAuth } from '../../auth/useAuth';
import { AuthProgress } from './AuthProgress';
import { useAuthGuard } from './useAuthGuard';
import config from '../../config/config';

// Allowed URLs when subscription plan is expired
const ALLOWED_URLS = ['/debug'];

const AuthGuard: FC<GuardProps> = ({ children, action }) => {
	const intl = useIntl();
	const navigate = useNavigate();
	const location = useLocation();
	const { organizationSlug } = useOrganizationDomainContext();
	const auth = useAuth();
	const { isAuthenticated, hasOrganizationAccess, isActionAllowed, isTrialExpired } = useAuthGuard(action);
	const { openSnackbar } = useSnackbar();
	const expiredPageEnabled = false; // @todo

	const pass = isAuthenticated && isActionAllowed;

	const signOut = useSignOut(() => {
		openSnackbar({
			open: true,
			message: 'Unexpected error.',
			variant: 'alert',
			alert: {
				color: 'error',
			},
			close: true,
		});
	});

	useEffect(() => {
		if (!auth.isLoading && auth.isSignedIn && hasOrganizationAccess === false) {
			openSnackbar({
				open: true,
				message: intl.formatMessage(
					{ id: 'auth-guard:organization-denied' },
					{
						organizationSlug,
					},
				),
				variant: 'alert',
				alert: {
					color: 'error',
				},
				close: true,
			});
			if (config.environment !== 'local') {
				signOut();
			} else {
				window.location.replace(getIdUrl('organization-denied'));
			}
		}

		if (!auth.isLoading && !auth.isSignedIn) {
			openSnackbar({
				open: true,
				message: intl.formatMessage({ id: 'common.logout' }),
				variant: 'alert',
				alert: {
					color: 'info',
				},
				close: true,
			});
			navigate('login', {
				state: {
					from: location.pathname,
				},
				replace: true,
			});
		}
	}, [organizationSlug, signOut, intl, navigate, location, auth, hasOrganizationAccess, openSnackbar]);

	useEffect(() => {
		if (pass) {
			if (ALLOWED_URLS.some((url) => location.pathname.startsWith(url)) || !expiredPageEnabled) {
				return;
			} else if (!isTrialExpired && location.pathname.startsWith('/expired')) {
				navigate('/');
			} else if (isTrialExpired && !location.pathname.startsWith('/expired')) {
				navigate('expired');
			}
		}
	}, [pass, isTrialExpired, navigate, location.pathname, expiredPageEnabled]);

	return pass ? children : <AuthProgress />;
};

const getIdUrl = (reason?: string): string => {
	return reason ? `https://id.outofdark.site/?reason=${reason}` : 'https://id.outofdark.site';
};

export default AuthGuard;
